import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';
import { buildApiUrl } from '../../config/apiConfig';
import './CourseCertificatePage.css';
import { logError } from '../../utils/logger';

const CourseCertificatePage = () => {
  const { courseId } = useParams();
  const { user, token, isAuthenticated } = useAuth();
  const { notify } = useNotifications();
  const navigate = useNavigate();
  const [courseTitle, setCourseTitle] = useState('');
  const [loading, setLoading] = useState(true);
  const [pdfBusy, setPdfBusy] = useState(false);
  const certRef = useRef(null);

  useEffect(() => {
    document.body.classList.add('cert-printing');
    return () => {
      document.body.classList.remove('cert-printing');
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !token) {
      navigate('/login', {
        state: { from: `/my-courses/${courseId}/certificate` },
      });
      return;
    }

    const load = async () => {
      setLoading(true);
      try {
        const progRes = await fetch(buildApiUrl(`/api/user/courses/progress/${courseId}`), {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!progRes.ok) {
          throw new Error('No se pudo verificar tu progreso en este curso.');
        }
        const prog = await progRes.json();
        if (!prog.certificateEligible) {
          notify({
            message: 'Aún no podés obtener el certificado de este curso.',
            type: 'warning',
          });
          navigate('/my-courses');
          return;
        }

        const courseRes = await fetch(buildApiUrl(`/api/courses/${courseId}`), {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!courseRes.ok) {
          throw new Error('No se pudo cargar el curso.');
        }
        const courseData = await courseRes.json();
        const c = courseData.course || courseData;
        setCourseTitle(c.titulo || c.nombre || 'Curso');
      } catch (err) {
        logError('[CourseCertificatePage]', err);
        notify({
          message: err.message || 'Error al cargar el certificado.',
          type: 'error',
        });
        navigate('/my-courses');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [courseId, isAuthenticated, token, navigate, notify]);

  const fullName = [user?.nombre, user?.apellido].filter(Boolean).join(' ').trim()
    || user?.name
    || 'Estudiante';

  const issuedDate = new Date().toLocaleDateString('es-AR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const handleDownloadPdf = async () => {
    const el = certRef.current;
    if (!el) return;
    setPdfBusy(true);
    try {
      if (document.fonts?.ready) {
        await document.fonts.ready;
      }
      const { default: html2pdf } = await import('html2pdf.js');
      const filename = `certificado-always-aligners-${courseId}.pdf`;
      await html2pdf()
        .set({
          margin: [12, 12, 12, 12],
          filename,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: {
            scale: 2,
            useCORS: true,
            logging: false,
            letterRendering: true,
            scrollY: 0,
            scrollX: 0,
          },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
          pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
        })
        .from(el)
        .save();
    } catch (err) {
      logError('[CourseCertificatePage] handleDownloadPdf', err);
      notify({
        message: 'No se pudo generar el PDF. Probá de nuevo o usá Imprimir.',
        type: 'error',
      });
    } finally {
      setPdfBusy(false);
    }
  };

  if (!isAuthenticated || !user) {
    return null;
  }

  if (loading) {
    return (
      <div className="cert-page cert-page--loading">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Cargando...</span>
        </div>
        <p>Preparando tu certificado...</p>
      </div>
    );
  }

  return (
    <div className="cert-page">
      <div className="cert-toolbar no-print">
        <button type="button" className="cert-btn cert-btn--ghost" onClick={() => navigate('/my-courses')} disabled={pdfBusy}>
          <i className="fas fa-arrow-left" aria-hidden />
          Volver a Mis cursos
        </button>
        <button
          type="button"
          className="cert-btn cert-btn--primary"
          onClick={handleDownloadPdf}
          disabled={pdfBusy}
        >
          {pdfBusy ? (
            <>
              <span className="spinner-border spinner-border-sm" role="status" aria-hidden />
              Generando PDF…
            </>
          ) : (
            <>
              <i className="fas fa-file-pdf" aria-hidden />
              Descargar PDF
            </>
          )}
        </button>
        <button
          type="button"
          className="cert-btn cert-btn--ghost"
          onClick={() => window.print()}
          disabled={pdfBusy}
          title="En Chrome/Edge: en Más opciones desactivá «Encabezados y pies de página» para un PDF sin fecha ni URL."
        >
          <i className="fas fa-print" aria-hidden />
          Imprimir
        </button>
      </div>

      <article ref={certRef} className="cert-frame" aria-label="Certificado de finalización de curso">
        <div className="cert-frame-inner">
          <header className="cert-header">
            <p className="cert-brand">Always Aligners System</p>
          </header>

          <p className="cert-lead">Este certificado acredita que:</p>

          <p className="cert-body">
            <span className="cert-name">{fullName}</span>
            {' '}
            ha participado con éxito en el curso
            {' '}
            <span className="cert-course-title">{courseTitle}</span>
            .
          </p>

          <p className="cert-filler">
            Ha cumplido con los requisitos del programa y la aprobación de las evaluaciones, conforme a la
            formación continua de Always Aligners System.
          </p>

          <p className="cert-date cert-date--inline">
            <span className="cert-date-label">Fecha de emisión: </span>
            {issuedDate}
          </p>

          <div className="cert-sign-block">
            <p className="cert-signature-script">Esp. Patricia Tarquini</p>
            <p className="cert-signature-print">Patricia Tarquini</p>
            <p className="cert-signature-creds">
              Od. Especialista en Ortodoncia - Ortopedia - Diagnóstico por Imagen
            </p>
          </div>
        </div>
      </article>
    </div>
  );
};

export default CourseCertificatePage;
